import React, { useState, useTransition } from 'react'

import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom'

import * as Yup from 'yup'
import { Field, Form, Formik, FormikHelpers } from 'formik'

import { Box, Divider, Stack, Typography, Link, Button, Alert } from '@mui/material'

import { TextInput } from '@/components/Inputs/Text'
import { MaskedInput } from '@/components/Inputs/Masked'
import BiometricLoginButton from '@/components/BiometricLoginButton'
import { BiometricEnablePrompt } from '@/components/BiometricEnablePrompt'

import { useAuth } from '@/hooks/useAuth'
import { useBiometricLogin } from '@/hooks/useBiometricLogin'

import { HttpError } from '@/api/http'

import { useBackButton } from '@/hooks/useBackButton'
import { PasswordInput } from '@/components/Inputs/Password'

export interface LoginFormData {
  username: string
  password: string
}

const initialValues: LoginFormData = {
  username: '',
  password: '',
}

const CPF_AUTOFILL_ANIMATION_NAME = 'cpfInputAutoFillDetect'

const formatCPF = (rawValue: string): string => {
  const digits = rawValue.replace(/\D/g, '').slice(0, 11)

  return digits
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2')
}

const validationSchema = Yup.object().shape({
  username: Yup.string()
    .test('is-valid-cpf', 'O CPF informado é inválido', (value) => {
      if (!value) return false

      return /^\d{3}\.\d{3}\.\d{3}-\d{2}$/.test(value)
    })
    .required('O campo de CPF é obrigatório'),
  password: Yup.string().required('O campo de senha é obrigatório'),
})

const AuthLoginPage: React.FC = () => {
  useBackButton()

  const navigate = useNavigate()
  const { state } = useLocation()

  const { login, isLoading: isLoadingAuth } = useAuth()
  const [isPending, startTransition] = useTransition()

  const [loginError, setLoginError] = useState<string | null>(null)

  const navigateAfterLogin = () => {
    navigate(state?.from ? state.from : '/')
  }

  const biometricLogin = useBiometricLogin({ onSuccess: navigateAfterLogin })

  const handleOnSubmitLogin = (values: LoginFormData, { setSubmitting }: FormikHelpers<LoginFormData>) => {
    startTransition(async () => {
      try {
        await login(values.username, values.password)
        await biometricLogin.triggerBiometricPrompt()
      } catch (error) {
        const httpError = error as HttpError
        setLoginError(
          httpError.message || 'Ocorreu um erro inesperado ao tentar fazer login! Tente novamente mais tarde.'
        )
      } finally {
        setSubmitting(false)
      }
    })
  }

  const handleOnClickRegister = () => {
    navigate('/auth/register', { state })
  }

  return (
    <Stack
      direction="column"
      alignItems="center"
      justifyContent="center"
      spacing={4}
      sx={{ maxWidth: 400, margin: '0 auto' }}
    >
      <Stack direction="column" alignItems="center" textAlign="center">
        <Typography variant="h6" fontWeight="bold">
          Entrar na MicroRED
        </Typography>
        <Typography variant="subtitle1">Carregue fácil, Ande mais!</Typography>
      </Stack>

      {biometricLogin.showLoginButton && !biometricLogin.isChecking ? (
        <Stack direction="column" spacing={2} sx={{ width: '100%' }}>
          <BiometricLoginButton onSuccess={navigateAfterLogin} disabled={isLoadingAuth || isPending} />
          <Divider>ou</Divider>
        </Stack>
      ) : null}

      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleOnSubmitLogin}>
        {({ setFieldValue }) => {
          const handleCPFAutoFillDetected = (event: React.AnimationEvent<HTMLInputElement>) => {
            if (event.animationName !== CPF_AUTOFILL_ANIMATION_NAME) {
              return
            }

            setFieldValue('username', formatCPF(event.currentTarget.value))
          }

          return (
            <Box component={Form} id="login-form" name="login" width="100%">
              <Stack direction="column" spacing={2} sx={{ width: '100%' }}>
                {loginError ? (
                  <Alert severity="error" sx={{ width: '100%', alignItems: 'center', borderRadius: 4 }}>
                    {loginError}
                  </Alert>
                ) : null}

                <Field
                  component={TextInput}
                  label="CPF"
                  placeholder="000.000.000-00"
                  id="username"
                  name="username"
                  autoComplete="username"
                  sx={{
                    '@keyframes cpfInputAutoFillDetect': { from: {}, to: {} },
                    '& input:-webkit-autofill': {
                      animationName: CPF_AUTOFILL_ANIMATION_NAME,
                      animationDuration: '0.001s',
                    },
                  }}
                  InputProps={{
                    inputComponent: MaskedInput,
                    inputProps: {
                      inputMode: 'numeric',
                      mask: '___.___.___-__',
                      replacement: {
                        _: /[0-9]/,
                      },
                      onAnimationStart: handleCPFAutoFillDetected,
                    },
                  }}
                />
                <PasswordInput label="Senha" placeholder="Sua senha" autoComplete="current-password" name="password" />

                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  sx={{ borderRadius: 20 }}
                  loading={isPending}
                  disabled={isPending}
                  disableElevation
                >
                  Entrar
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  fullWidth
                  sx={{ borderRadius: 20 }}
                  onClick={handleOnClickRegister}
                  disabled={isPending}
                  disableElevation
                >
                  Criar conta
                </Button>

                <Stack alignItems="center" justifyContent="center">
                  <Link component={RouterLink} to="/auth/forgot-password" state={state} variant="body2" underline="hover">
                    Esqueceu a senha?
                  </Link>
                </Stack>
              </Stack>
            </Box>
          )
        }}
      </Formik>

      <BiometricEnablePrompt open={biometricLogin.enablePrompt.isOpen} onClose={biometricLogin.handleClosePrompt} />
    </Stack>
  )
}

export default AuthLoginPage
